import { useCallback } from "react";

import { useModals } from "hooks";

import { Characteristic, IFormContext, State } from "..";

interface AcceptParams {
  state: State;
  setState: React.Dispatch<React.SetStateAction<State>>;
}

type useCharHooks = (
  params: AcceptParams
) => Pick<IFormContext, "createChar" | "updateChar" | "deleteChar" | "getCharacteristics">;

export const useCharHooks: useCharHooks = ({ state, setState }) => {
  const { deleteModal } = useModals();

  const createChar = useCallback<IFormContext["createChar"]>(
    (params) => {
      const maxId = state.characteristics.reduce((acc, item) => {
        const numberId = Number(item.id);
        return numberId > acc ? numberId : acc;
      }, 0);

      const newChar: Characteristic = {
        id: String(maxId + 1),
        category_name: state.category,
        is_show: false,
        is_deleted: false,
        is_locked_to_edit: false,
        subcategory_ids: [],
        list_options: [],
        required_to_status_ids: null,
        ...params
      };

      setState((prevState) => ({
        ...prevState,
        characteristics: [...prevState.characteristics, newChar]
      }));
    },
    [state, setState]
  );

  const updateChar = useCallback<IFormContext["updateChar"]>(
    ({ id, ...params }) => {
      setState((prevState) => ({
        ...prevState,
        characteristics: prevState.characteristics.map((item) => (item.id === id ? { ...item, ...params } : item))
      }));
    },
    [state, setState]
  );

  const deleteChar = useCallback<IFormContext["deleteChar"]>(
    ({ id, name }) => {
      deleteModal({
        message: name,
        action: async () => {
          setState((prevState) => {
            let updatedCharacteristics = prevState.characteristics;

            // delete new characteristic
            if (typeof id === "string") {
              updatedCharacteristics = prevState.characteristics.filter((item) => item.id !== id);
            } else {
              // delete existing characteristic
              updatedCharacteristics = prevState.characteristics.map((item) =>
                item.id === id ? { ...item, is_deleted: true } : item
              );
            }

            return {
              ...prevState,
              characteristics: updatedCharacteristics,
              data: prevState.data.map((item) => (item.characteristic_id === id ? { ...item, is_deleted: true } : item))
            };
          });
        }
      });
    },
    [state, setState, deleteModal]
  );

  const getCharacteristics = useCallback<IFormContext["getCharacteristics"]>(
    (where) => {
      if (!where) return state.characteristics;
      if (!Object.entries(where).length) return state.characteristics;

      const whereParams = Object.keys(where);

      return state.characteristics.filter((entity) => {
        let coincidences = 0;

        whereParams.forEach((key) => {
          const queryValue = where[key as keyof Pick<Characteristic, "category_name" | "subcategory_ids">];
          // Types issue - the keys of E includes keys of Q
          const entityValue = (entity as Characteristic)[key as keyof Characteristic];

          if (key === "subcategory_ids" && Array.isArray(queryValue)) {
            // Handle subcategory_ids array filtering
            const subcategoryIds = queryValue as number[];
            if (!subcategoryIds.length) {
              // If empty array provided, match characteristics with empty subcategory_ids
              if (entityValue && Array.isArray(entityValue) && !entityValue.length) {
                ++coincidences;
              }
            } else {
              // If non-empty array provided, check if any of the provided IDs are included
              const entitySubcategoryIds = entityValue as number[];
              const hasMatchingSubcategory = subcategoryIds.some(
                (subcategoryId) => Array.isArray(entitySubcategoryIds) && entitySubcategoryIds.includes(subcategoryId)
              );
              if (hasMatchingSubcategory) {
                ++coincidences;
              }
            }
          } else if (queryValue === entityValue) {
            ++coincidences;
          }
        });

        return coincidences === whereParams.length;
      });
    },
    [state]
  );

  return {
    createChar,
    updateChar,
    deleteChar,
    getCharacteristics
  };
};
